import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import moment from "moment";
import { Dispatch, SetStateAction, useContext } from "react";
import { CustomEvent } from "schemas/functions";

import StyledSelect from "components/Styled/Select";
import TimezoneAutocomplete from "components/TimezoneAutocomplete";
import { getLocation } from "utils/influencer";
import { isDaysInFuture } from "utils/time";
import { trackEvent } from "utils/tracking";

import {
  Day,
  convertTimeToUtc,
  getDateOptions,
  getFutureHours,
} from "./helpers";

interface Props {
  setDatePicked: Dispatch<SetStateAction<Day>>;
  setTimePicked: Dispatch<
    React.SetStateAction<{
      start: string;
      end: string;
    }>
  >;
  setCustomDatePicked: Dispatch<
    React.SetStateAction<{
      start: string;
      end: string;
    }>
  >;
  timezone?: string;
  datePicked: Day;
  timePicked: {
    start: string;
    end: string;
  };
  customDatePicked: {
    start: string;
    end: string;
  };
  dateOptions: Day[];
  validHours: string[];
}

const fieldLabel = {
  fontSize: 12,
  fontWeight: 500,
  color: "rgba(1,67,57,0.7)",
  mb: 0.5,
};

// Match StyledSelect's outline radius (sx `borderRadius: 4` = 16px via
// theme.shape) so the Autocomplete + date inputs match the other selectors.
const outlinedInputSx = {
  "& fieldset.MuiOutlinedInput-notchedOutline": { borderRadius: 4 },
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Typography sx={fieldLabel}>{label}</Typography>
    {children}
  </Box>
);

const TimeOption = ({
  setDatePicked,
  setTimePicked,
  setCustomDatePicked,
  datePicked,
  timePicked,
  customDatePicked,
  timezone,
  dateOptions,
  validHours,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const { setEmailSettings, profile } = useContext(OrganizationUserContext);
  const profileLocation = getLocation(profile);

  const handleTimezoneChange = (
    event: CustomEvent,
    newValue: string | null,
  ) => {
    if (newValue) {
      trackEvent("Timezone Changed", { timezone: newValue });
      setEmailSettings((prev) =>
        prev
          ? {
              ...prev,
              timezone: newValue,
            }
          : prev,
      );

      const validDates = getDateOptions(newValue);
      if (!validDates?.dateOptions?.includes(datePicked)) {
        setDatePicked(validDates?.dateOptions?.[0]);
      }
    }
  };

  const handleChange = (e: CustomEvent) => {
    const { value } = e.target;
    trackEvent("Bulk Schedule Date Selected", { Option: value });
    setDatePicked(value);
    const validHours = getFutureHours(value, timezone || moment.tz.guess());
    if (timePicked.start && !validHours.includes(timePicked.start)) {
      setTimePicked((prev) => ({
        ...prev,
        start: validHours[0],
      }));
    }
    if (timePicked.end && !validHours.includes(timePicked.end)) {
      setTimePicked((prev) => ({
        ...prev,
        end: validHours[validHours.length - 1],
      }));
    }
  };

  const handlePickTime = (e: CustomEvent, name: string) => {
    const { value } = e.target;
    trackEvent("Bulk Schedule Time Selected", { Option: value });
    setTimePicked((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectCustomDate = (e: CustomEvent, name: string) => {
    const { value } = e.target;
    if (value && !isDaysInFuture(value)) {
      setAlert("Please pick a date in future", "error");
      return;
    }
    setCustomDatePicked((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { selectedDate } = convertTimeToUtc(
    datePicked,
    customDatePicked,
    timePicked,
    timezone || moment.tz.guess(),
  );

  return (
    <Box>
      {profileLocation && (
        <Typography
          variant="body2"
          sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)", mb: 2 }}
        >
          Your current profile location is <strong>{profileLocation}</strong>.
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field
            label={`Send window${selectedDate ? ` · ${selectedDate}` : ""}`}
          >
            <StyledSelect
              onChange={handleChange}
              value={datePicked}
              size="small"
              fullWidth
            >
              {dateOptions?.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {lodash.startCase(item)}
                </MenuItem>
              ))}
            </StyledSelect>
          </Field>
        </Grid>

        <Grid item xs={12} md={6}>
          <Field label="Timezone">
            <TimezoneAutocomplete
              value={timezone}
              onChange={handleTimezoneChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  sx={outlinedInputSx}
                />
              )}
              disableClearable
              size="small"
              fullWidth
            />
          </Field>
        </Grid>

        {datePicked === "customized" && (
          <>
            <Grid item xs={12} md={6}>
              <Field label="Start date">
                <TextField
                  type="date"
                  value={customDatePicked?.start}
                  onChange={(e) => selectCustomDate(e, "start")}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={outlinedInputSx}
                />
              </Field>
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="End date">
                <TextField
                  type="date"
                  value={customDatePicked?.end}
                  onChange={(e) => selectCustomDate(e, "end")}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={outlinedInputSx}
                />
              </Field>
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <Field label="Start time">
            <StyledSelect
              onChange={(e: CustomEvent) => handlePickTime(e, "start")}
              value={timePicked?.start}
              displayEmpty
              size="small"
              fullWidth
            >
              <MenuItem value="">Pick start time</MenuItem>
              {validHours?.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </StyledSelect>
          </Field>
        </Grid>

        <Grid item xs={12} md={6}>
          <Field label="End time">
            <StyledSelect
              onChange={(e: CustomEvent) => handlePickTime(e, "end")}
              value={timePicked?.end}
              displayEmpty
              size="small"
              fullWidth
            >
              <MenuItem value="">Pick end time</MenuItem>
              {validHours?.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </StyledSelect>
          </Field>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TimeOption;
